var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useErrorHandler, ErrorBoundary } from "react-error-boundary";
import { useThemeName, IconTick, Columns, Column, Text, Box, Stack, useResponsiveValue, Heading, TooltipRenderer, IconInfo, Divider, Button, Inline, TextLinkButton, Loader } from "braid-design-system";
import * as React from "react";
import { createContext, useContext, useRef, useEffect, useCallback } from "react";
import { DisplayRecommendation, useAdBudgetProductsForUpsellQuery } from "./graphql-types.chunk.mjs";
import { AdType, ProductTypeInput } from "./graphql-types.chunk.mjs";
import { useTranslations } from "@vocab/react";
import { formatMinorUnits } from "@seek/currency";
import { translations as translations$1 } from "./components/BudgetOption/.vocab/index.mjs";
import { DiscountBadges } from "./styles/components/BudgetOption/Features/DiscountBadges.mjs";
import { translations } from "./components/BudgetOption/Features/.vocab/index.mjs";
import { translations as translations$2 } from "./components/Footer/.vocab/index.mjs";
import { translations as translations$3 } from "./components/Upsell/.vocab/index.mjs";
import { useApolloClient } from "@apollo/client";
import { ShoppingCartContext } from "@seek/shopping-cart-provider";
const getPriceWithTax = (budgetValue, currency, taxDisplayRecommendation, taxValue) => {
  if (taxValue && taxDisplayRecommendation && taxDisplayRecommendation === DisplayRecommendation.inclusive) {
    return {
      value: budgetValue + taxValue,
      currency
    };
  }
  return {
    value: budgetValue,
    currency
  };
};
const mapAdBudgetOptionsToTrackingPayload = (adBudgetOptions) => adBudgetOptions.map((adBudgetOption, position) => ({
  position: position + 1,
  id: adBudgetOption.budgetId,
  price: adBudgetOption.budgetValue,
  currency: adBudgetOption.currency,
  type: adBudgetOption.budgetType
}));
const mapBudgetOptionsToHubblePayload = (adBudgetOptions) => adBudgetOptions.map(
  ({
    budgetId,
    budgetValue,
    taxValue,
    taxDisplayRecommendation,
    currency
  }) => ({
    productId: budgetId,
    netPrice: getPriceWithTax(
      budgetValue,
      currency,
      taxDisplayRecommendation,
      taxValue
    )
  })
);
const sendImpressionTracking = (props) => {
  const {
    adBudgetOptions,
    productSetId,
    opportunityId,
    onTrackingCallback,
    hubbleTrackEventHandler,
    useUnifiedTracking
  } = props;
  onTrackingCallback({
    ...useUnifiedTracking ? { eventName: "adbudget_upsell_displayed" } : { eventType: "ecaja_adbudget_upsell_viewed" },
    contractProductSetId: productSetId,
    adProductSetId: productSetId,
    opportunityId,
    contractProductPriceQuery: mapAdBudgetOptionsToTrackingPayload(adBudgetOptions)
  });
  if (hubbleTrackEventHandler) {
    hubbleTrackEventHandler("upsell_products_displayed", {
      widgetName: "BudgetUpsell",
      products: mapBudgetOptionsToHubblePayload(adBudgetOptions)
    });
  }
};
const BaselineText = (props) => /* @__PURE__ */ jsx(Text, { ...props, baseline: false });
const TextWithIcon = ({ icon, ...restProps }) => /* @__PURE__ */ jsxs(Columns, { space: "xsmall", children: [
  /* @__PURE__ */ jsx(Column, { width: "content", children: icon }),
  /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(BaselineText, { ...restProps }) })
] });
const IconTickText = (props) => {
  const theme = useThemeName();
  return /* @__PURE__ */ jsx(
    TextWithIcon,
    {
      ...props,
      icon: /* @__PURE__ */ jsx(
        IconTick,
        {
          size: "xsmall",
          tone: theme === "apac" ? "link" : "formAccent"
        }
      )
    }
  );
};
const Features = ({
  purchasableProducts,
  adBudgetDuration
}) => {
  const { t } = useTranslations(translations);
  const keyFeatureHighlight = Array.isArray(purchasableProducts) ? t("Post productListExceptLast and lastProduct ads", {
    productListExceptLast: purchasableProducts.slice(0, -1).join(", "),
    lastProduct: purchasableProducts.slice(-1)[0]
  }) : purchasableProducts === "Any" && t("Post any ad type");
  return /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
    /* @__PURE__ */ jsx(IconTickText, { children: keyFeatureHighlight }),
    /* @__PURE__ */ jsx(IconTickText, { children: t("Discount applies for...", {
      adBudgetDuration
    }) })
  ] }) });
};
const BudgetOption = (props) => {
  var _a;
  const theme = useThemeName();
  const { t } = useTranslations(translations$1);
  const {
    adBudget,
    productSetId,
    opportunityId,
    isSelected,
    onSelectedBudgetChange,
    onTrackingCallback,
    onBudgetOptionClicked,
    hubbleTrackEventHandler,
    mostNumberOfDiscounts,
    useUnifiedTracking,
    adBudgetOptions,
    loadingProductIds
  } = props;
  const isLoading = Boolean(loadingProductIds == null ? void 0 : loadingProductIds.includes(productSetId));
  const {
    budgetValue,
    budgetId,
    duration,
    taxValue,
    taxCode,
    taxDisplayRecommendation,
    budgetName
  } = adBudget;
  const onSelect = () => {
    if (isLoading) return;
    if (!isSelected) {
      onSelectedBudgetChange(budgetId, budgetName, budgetValue, "select");
      onTrackingCallback({
        ...useUnifiedTracking ? {
          eventName: "ad_budget_pressed",
          linkAction: "select",
          productEvents: "add",
          contractProductSetProductId: budgetId,
          contractProductPriceQuery: mapAdBudgetOptionsToTrackingPayload(adBudgetOptions),
          contractProductName: budgetName,
          opportunityId
        } : {
          eventType: "ecaja_ad_budget_selected",
          productEvents: ["add"],
          productIds: [budgetId]
        },
        productCategories: ["contract"],
        productQuantities: [1],
        productTotalPrice: budgetValue
      });
      if (hubbleTrackEventHandler) {
        hubbleTrackEventHandler("upsell_product_selected", {
          widgetName: "BudgetUpsell",
          productSetId,
          productId: budgetId
        });
      }
      if (onBudgetOptionClicked) {
        onBudgetOptionClicked(budgetValue, "select");
      }
    }
    if (isSelected) {
      onSelectedBudgetChange(null, null, budgetValue, "deselect");
      onTrackingCallback({
        ...useUnifiedTracking ? {
          eventName: "ad_budget_pressed",
          linkAction: "remove",
          productEvents: "remove",
          contractProductSetProductId: budgetId,
          contractProductPriceQuery: mapAdBudgetOptionsToTrackingPayload(adBudgetOptions),
          contractProductName: budgetName,
          opportunityId
        } : {
          eventType: "ecaja_ad_budget_deselected",
          productEvents: ["remove"],
          productIds: [budgetId]
        },
        productCategories: ["contract"],
        productQuantities: [1],
        productTotalPrice: budgetValue
      });
      if (onBudgetOptionClicked) {
        onBudgetOptionClicked(budgetValue, "deselect");
      }
    }
  };
  const durationUnitMapping = {
    YEAR: {
      singular: t("year"),
      plural: t("years")
    },
    MONTH: {
      singular: t("month"),
      plural: t("months")
    },
    WEEK: {
      singular: t("week"),
      plural: t("weeks")
    },
    DAY: {
      singular: t("day"),
      plural: t("days")
    },
    HOUR: {
      singular: t("hour"),
      plural: t("hours")
    },
    MINUTE: {
      singular: t("minute"),
      plural: t("minutes")
    },
    SECOND: {
      singular: t("second"),
      plural: t("seconds")
    }
  };
  const durationLabel = t("duration label", {
    durationValue: `${duration.value}`,
    durationUnit: durationUnitMapping[duration.unit.toUpperCase()][duration.value > 1 ? "plural" : "singular"]
  });
  const responsiveValue = useResponsiveValue();
  const isMobile = responsiveValue({
    mobile: true,
    tablet: false
  });
  const budgetDisplayPrice = taxValue && taxDisplayRecommendation === "inclusive" ? budgetValue + taxValue : budgetValue;
  let taxDescription = "";
  if (taxDisplayRecommendation === "inclusive") {
    taxDescription = `${t("incl.")} ${taxCode}`;
  }
  if (taxDisplayRecommendation === "exclusive") {
    taxDescription = `+ ${taxCode}`;
  }
  const numberOfDiscounts = ((_a = adBudget.discounts) == null ? void 0 : _a.length) ?? 0;
  const numberOfDiscountPadding = Math.abs(
    mostNumberOfDiscounts - numberOfDiscounts
  );
  const isCompactPrice = budgetDisplayPrice.toString().length >= 9;
  const viewProps = {
    ...props,
    onSelect,
    durationLabel,
    isMobile,
    budgetDisplayPrice,
    taxDescription,
    numberOfDiscounts,
    numberOfDiscountPadding,
    isCompactPrice,
    isLoading
  };
  return theme === "seekJobs" ? /* @__PURE__ */ jsx(BudgetOptionSEEKJOBS, { ...viewProps }) : /* @__PURE__ */ jsx(BudgetOptionAPAC, { ...viewProps });
};
const BudgetOptionAPAC = (props) => {
  const { t } = useTranslations(translations$1);
  const {
    locale,
    adBudget: {
      budgetId,
      adPostingFrequency,
      discounts,
      adRange: { minimum, maximum },
      currency,
      purchasableProducts
    },
    isSelected,
    durationLabel,
    isCompactPrice,
    isMobile,
    numberOfDiscountPadding,
    onSelect,
    taxDescription,
    budgetDisplayPrice,
    isLoading
  } = props;
  return /* @__PURE__ */ jsx(
    Box,
    {
      boxShadow: isSelected ? "borderFormAccentLarge" : "borderNeutralLight",
      padding: "medium",
      role: "AdBudgetOption",
      "data-role": "AdBudgetOption",
      height: "full",
      children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
        /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
          /* @__PURE__ */ jsx(Heading, { level: "3", weight: "regular", align: "center", children: t("Frequency label", {
            adPostingFrequency: adPostingFrequency === "Occasional" || adPostingFrequency === "Regular" || adPostingFrequency === "Frequent" ? adPostingFrequency : "other"
          }) }),
          /* @__PURE__ */ jsxs(Text, { size: "small", align: "center", tone: "secondary", children: [
            t("min max range of ads...", {
              br: () => isMobile ? /* @__PURE__ */ jsx(Fragment, {}) : /* @__PURE__ */ jsx("br", {}),
              minMaxAdRange: `${minimum}-${maximum}`,
              durationText: durationLabel
            }),
            /* @__PURE__ */ jsx(
              TooltipRenderer,
              {
                id: `${budgetId}-info-tool-tips`,
                tooltip: /* @__PURE__ */ jsx(Text, { children: t("The exact number of ads...") }),
                children: ({ triggerProps }) => /* @__PURE__ */ jsx(
                  Box,
                  {
                    "aria-label": `${budgetId}-info`,
                    display: "inlineBlock",
                    paddingLeft: "xxsmall",
                    ...triggerProps,
                    children: /* @__PURE__ */ jsx(IconInfo, {})
                  }
                )
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsx(Divider, {}),
        /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
          /* @__PURE__ */ jsxs(Heading, { level: "3", weight: "regular", align: "center", children: [
            formatMinorUnits(budgetDisplayPrice, {
              currency,
              locale,
              dropFraction: true,
              notation: isCompactPrice ? "compact" : "standard"
            }),
            isCompactPrice && /* @__PURE__ */ jsx(
              TooltipRenderer,
              {
                id: `${budgetId}-value-info-tool-tips`,
                tooltip: /* @__PURE__ */ jsx(Box, { padding: "xxsmall", children: /* @__PURE__ */ jsx(Text, { align: "center", children: t("Exact price", {
                  br: () => isMobile ? /* @__PURE__ */ jsx(Fragment, {}) : /* @__PURE__ */ jsx("br", {}),
                  exactPrice: formatMinorUnits(budgetDisplayPrice, {
                    currency,
                    locale,
                    dropFraction: true
                  })
                }) }) }),
                children: ({ triggerProps }) => /* @__PURE__ */ jsx(
                  Box,
                  {
                    "aria-label": `${budgetId}-value-info`,
                    display: "inlineBlock",
                    paddingLeft: "xxsmall",
                    ...triggerProps,
                    children: /* @__PURE__ */ jsx(Text, { size: "xsmall", tone: "secondary", children: /* @__PURE__ */ jsx(IconInfo, {}) })
                  }
                )
              }
            )
          ] }),
          /* @__PURE__ */ jsx(Text, { size: "small", align: "center", tone: "secondary", children: taxDescription })
        ] }),
        /* @__PURE__ */ jsx(
          Button,
          {
            variant: isSelected ? "solid" : "ghost",
            onClick: onSelect,
            loading: isLoading,
            children: isSelected ? t("Remove") : t("Select")
          }
        ),
        discounts && /* @__PURE__ */ jsx(
          DiscountBadges,
          {
            discounts,
            currency,
            locale,
            discountPadding: numberOfDiscountPadding
          }
        ),
        /* @__PURE__ */ jsx(
          Features,
          {
            purchasableProducts,
            adBudgetDuration: durationLabel
          }
        )
      ] })
    }
  );
};
const BudgetOptionSEEKJOBS = (props) => {
  const { t } = useTranslations(translations$1);
  const {
    locale,
    adBudget: {
      budgetId,
      adPostingFrequency,
      discounts,
      adRange: { minimum, maximum },
      currency,
      purchasableProducts
    },
    isSelected,
    durationLabel,
    isCompactPrice,
    isMobile,
    numberOfDiscountPadding,
    onSelect,
    taxDescription,
    budgetDisplayPrice
  } = props;
  return /* @__PURE__ */ jsx(
    Box,
    {
      boxShadow: isSelected ? "borderFormAccentLarge" : "borderNeutralLight",
      paddingX: "small",
      paddingY: "xxlarge",
      role: "AdBudgetOption",
      "data-role": "AdBudgetOption",
      height: "full",
      borderRadius: "large",
      children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
        /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
          /* @__PURE__ */ jsx(Heading, { level: "3", weight: "regular", children: t("Frequency label", {
            adPostingFrequency: adPostingFrequency === "Occasional" || adPostingFrequency === "Regular" || adPostingFrequency === "Frequent" ? adPostingFrequency : "other"
          }) }),
          /* @__PURE__ */ jsxs(Text, { size: "small", tone: "secondary", children: [
            t("min max range of ads...", {
              br: () => /* @__PURE__ */ jsx(Fragment, {}),
              minMaxAdRange: `${minimum}-${maximum}`,
              durationText: durationLabel
            }),
            /* @__PURE__ */ jsx(
              TooltipRenderer,
              {
                id: `${budgetId}-info-tool-tips`,
                tooltip: /* @__PURE__ */ jsx(Text, { children: t("The exact number of ads...") }),
                children: ({ triggerProps }) => /* @__PURE__ */ jsx(
                  Box,
                  {
                    "aria-label": `${budgetId}-info`,
                    display: "inlineBlock",
                    paddingLeft: "xxsmall",
                    ...triggerProps,
                    children: /* @__PURE__ */ jsx(IconInfo, { tone: "neutral", alignY: "lowercase" })
                  }
                )
              }
            )
          ] })
        ] }),
        discounts && /* @__PURE__ */ jsx(
          DiscountBadges,
          {
            discounts,
            currency,
            locale,
            discountPadding: numberOfDiscountPadding
          }
        ),
        /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
          /* @__PURE__ */ jsxs(Heading, { level: "2", component: "h3", weight: "regular", children: [
            formatMinorUnits(budgetDisplayPrice, {
              currency,
              locale,
              dropFraction: true,
              notation: isCompactPrice ? "compact" : "standard"
            }),
            isCompactPrice && /* @__PURE__ */ jsx(
              TooltipRenderer,
              {
                id: `${budgetId}-value-info-tool-tips`,
                tooltip: /* @__PURE__ */ jsx(Box, { padding: "xxsmall", children: /* @__PURE__ */ jsx(Text, { children: t("Exact price", {
                  br: () => isMobile ? /* @__PURE__ */ jsx(Fragment, {}) : /* @__PURE__ */ jsx("br", {}),
                  exactPrice: formatMinorUnits(budgetDisplayPrice, {
                    currency,
                    locale,
                    dropFraction: true
                  })
                }) }) }),
                children: ({ triggerProps }) => /* @__PURE__ */ jsx(
                  Box,
                  {
                    "aria-label": `${budgetId}-value-info`,
                    display: "inlineBlock",
                    paddingLeft: "xxsmall",
                    ...triggerProps,
                    children: /* @__PURE__ */ jsx(Text, { size: "xsmall", tone: "secondary", children: /* @__PURE__ */ jsx(IconInfo, {}) })
                  }
                )
              }
            )
          ] }),
          /* @__PURE__ */ jsx(Text, { size: "small", tone: "secondary", children: taxDescription })
        ] }),
        /* @__PURE__ */ jsx(
          Button,
          {
            tone: "formAccent",
            variant: isSelected ? "solid" : "ghost",
            onClick: onSelect,
            children: isSelected ? t("Remove") : t("Select")
          }
        ),
        /* @__PURE__ */ jsx(
          Features,
          {
            purchasableProducts,
            adBudgetDuration: durationLabel
          }
        )
      ] })
    }
  );
};
const FLEXIBLE_BUDGET = "FlexibleBudget";
const BRANDED_BUDGET = "BrandedBudget";
const LocaleContext = createContext("AU");
const TOUR_ID_AU_PRODUCTION = "203166";
const TOUR_ID_AU_DEV = "203165";
const TOUR_ID_NZ_PRODUCTION = "208562";
const TOUR_ID_NZ_DEV = "208564";
const isProduction = () => !/\.staging|local|localhost$/.test(window.location.host);
const getTourId = (locale) => {
  const localeLower = locale.toLowerCase();
  if (localeLower.includes("au")) {
    return isProduction() ? TOUR_ID_AU_PRODUCTION : TOUR_ID_AU_DEV;
  }
  if (localeLower.includes("nz")) {
    return isProduction() ? TOUR_ID_NZ_PRODUCTION : TOUR_ID_NZ_DEV;
  }
  return null;
};
const openTour = (locale) => {
  const tourId = getTourId(locale);
  if (window.Intercom && tourId) {
    window.Intercom("startTour", tourId);
  }
};
const LearnMoreAboutVariablePricing = ({ onTrackingCallback, useUnifiedTracking }) => {
  const locale = useContext(LocaleContext);
  const localeLower = locale.toLowerCase();
  const { t } = useTranslations(translations$2);
  const theme = useThemeName();
  return /* @__PURE__ */ jsxs(Inline, { space: "xsmall", children: [
    /* @__PURE__ */ jsx(Text, { tone: "secondary", size: "small", children: t("All job ads on SEEK are variably...") }),
    /* @__PURE__ */ jsx(Text, { tone: "secondary", size: "small", children: (localeLower.includes("au") || localeLower.includes("nz")) && /* @__PURE__ */ jsx(
      TextLinkButton,
      {
        weight: theme === "apac" ? "regular" : "weak",
        onClick: () => {
          onTrackingCallback({
            ...useUnifiedTracking ? { eventName: "adbudget_prices_and_averages_help_pressed" } : {
              eventType: "ecaja_adbudget_prices_and_averages_help_interacted"
            }
          });
          openTour(locale);
        },
        children: t("Learn more about how pricing works.")
      }
    ) })
  ] });
};
const FlexibleFinePrint = ({ onTrackingCallback, useUnifiedTracking }) => /* @__PURE__ */ jsx(Box, { "data-role": "FinePrint", children: /* @__PURE__ */ jsx(
  LearnMoreAboutVariablePricing,
  {
    onTrackingCallback,
    useUnifiedTracking
  }
) });
const BrandedFinePrint = ({ onTrackingCallback, useUnifiedTracking }) => /* @__PURE__ */ jsx(Box, { "data-role": "FinePrint", children: /* @__PURE__ */ jsx(
  LearnMoreAboutVariablePricing,
  {
    onTrackingCallback,
    useUnifiedTracking
  }
) });
const getFinePrint = (adBudgetType, onTrackingCallback, useUnifiedTracking) => {
  switch (adBudgetType) {
    case FLEXIBLE_BUDGET:
      return /* @__PURE__ */ jsx(
        FlexibleFinePrint,
        {
          onTrackingCallback,
          useUnifiedTracking
        }
      );
    case BRANDED_BUDGET:
      return /* @__PURE__ */ jsx(
        BrandedFinePrint,
        {
          onTrackingCallback,
          useUnifiedTracking
        }
      );
    default:
      return null;
  }
};
const Footer = ({ adBudgetType, locale, onTrackingCallback, useUnifiedTracking }) => /* @__PURE__ */ jsx(LocaleContext.Provider, { value: locale, children: getFinePrint(adBudgetType, onTrackingCallback, useUnifiedTracking) });
const Layout = ({ styles, children }) => /* @__PURE__ */ jsx(Box, { background: "surface", ...styles, children });
const UpsellView = ({
  adBudgetOptions,
  adBudgetType,
  mostNumberOfDiscounts,
  isQueryLoading,
  shouldHideUpsell,
  theme,
  selectedBudgetProductId,
  onSelectedBudgetChange,
  onTrackingCallback,
  hubbleTrackEventHandler,
  onBudgetOptionClicked,
  styles,
  locale,
  productSetId,
  opportunityId,
  loadingProductIds,
  useUnifiedTracking = false
}) => {
  const { t } = useTranslations(translations$3);
  const isLoadingAndHasNoExistingData = isQueryLoading && !(adBudgetOptions == null ? void 0 : adBudgetOptions.length);
  if (isLoadingAndHasNoExistingData) {
    return /* @__PURE__ */ jsx(Layout, { styles, "data-role": "Loader", children: /* @__PURE__ */ jsx(Loader, { delayVisibility: true }) });
  }
  if (shouldHideUpsell) {
    return null;
  }
  return /* @__PURE__ */ jsx(Layout, { styles, "data-testid": "upsell", children: /* @__PURE__ */ jsxs(Stack, { space: theme === "apac" ? "gutter" : "medium", children: [
    /* @__PURE__ */ jsxs(Stack, { space: "medium", align: "left", children: [
      /* @__PURE__ */ jsx(Box, { display: "flex", alignItems: "center", justifyContent: "spaceBetween", children: /* @__PURE__ */ jsx(Heading, { level: theme === "apac" ? "3" : "4", children: t("Pay upfront and get a discount...") }) }),
      /* @__PURE__ */ jsx(Text, { size: "standard", align: "left", children: t("Choose an Ad Budget and use...") })
    ] }),
    /* @__PURE__ */ jsx(
      Columns,
      {
        collapseBelow: "tablet",
        space: theme === "apac" ? "none" : "medium",
        children: adBudgetOptions.map((adBudget) => /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(
          BudgetOption,
          {
            adBudgetOptions,
            adBudget,
            productSetId,
            opportunityId,
            isSelected: adBudget.budgetId === selectedBudgetProductId,
            onSelectedBudgetChange,
            onTrackingCallback,
            onBudgetOptionClicked,
            hubbleTrackEventHandler,
            mostNumberOfDiscounts,
            locale,
            useUnifiedTracking,
            loadingProductIds
          }
        ) }, adBudget.budgetId))
      }
    ),
    /* @__PURE__ */ jsx(
      Footer,
      {
        adBudgetType,
        locale,
        onTrackingCallback,
        useUnifiedTracking
      }
    )
  ] }) });
};
class GetProductSetError extends Error {
  constructor(message) {
    super();
    __publicField(this, "errorCode", "GET_PRODUCT_SET_ERROR");
    this.name = "GetProductSetError";
    this.message = message;
  }
}
class InvalidBudgetOptionForPresentationError extends Error {
  constructor(message) {
    super();
    __publicField(this, "errorCode", "INVALID_BUDGET_OPTION_FOR_PRESENTATION_ERROR");
    this.name = "InvalidBudgetOptionForPresentationError";
    this.message = message;
  }
}
const isAnz = (country) => ["au", "nz"].includes(country.toLowerCase());
const getDisplayAdType = (adType, country) => {
  if (adType === "CLASSIC" && isAnz(country)) {
    return "Classic";
  }
  if (adType === "CLASSIC") {
    return "Basic";
  }
  if ((adType === "BRANDED_UPGRADE" || adType === "BRANDED") && isAnz(country)) {
    return "StandOut";
  }
  if (adType === "BRANDED_UPGRADE" || adType === "BRANDED") {
    return "Branded";
  }
  if (adType === "PREMIUM") {
    return "Premium";
  }
  throw new InvalidBudgetOptionForPresentationError(`Invalid adType ${adType}`);
};
const createBudgetPresentation = (adBudgetProduct, country) => {
  var _a, _b, _c, _d;
  const { adPostingFrequency, adRange, purchasableProducts } = adBudgetProduct;
  if (adPostingFrequency !== "Occasional" && adPostingFrequency !== "Regular" && adPostingFrequency !== "Frequent") {
    throw new InvalidBudgetOptionForPresentationError(
      `Invalid adPostingFrequency ${adPostingFrequency}`
    );
  }
  const durationFeature = adBudgetProduct.features.flatMap(
    (feature) => (feature == null ? void 0 : feature.__typename) === "DurationFeature" ? [feature] : []
  )[0];
  if (durationFeature === void 0) {
    throw new InvalidBudgetOptionForPresentationError(
      `AdBudgetProduct ${adBudgetProduct.id} is missing duration features needed for presentation`
    );
  }
  const isBrandedFeaturesFeaturePresent = Boolean(
    adBudgetProduct.features.find(
      (feature) => (feature == null ? void 0 : feature.__typename) === "BrandedFeaturesFeature"
    )
  );
  const discounts = adBudgetProduct.features.flatMap(
    (feature) => (feature == null ? void 0 : feature.__typename) === "AdTypeDiscountsFeature" ? [
      feature.discounts.flatMap(
        (discountFeature) => {
          if (discountFeature.discount.discountPercentage > 0 && discountFeature.discount.discountAmount.value > 0) {
            throw new InvalidBudgetOptionForPresentationError(
              "Fixed and percentage discounts specified in single feature"
            );
          }
          if (discountFeature.discount.discountPercentage > 0) {
            return [
              {
                type: "Percentage",
                adType: getDisplayAdType(discountFeature.adType, country),
                value: discountFeature.discount.discountPercentage
              }
            ];
          }
          if (discountFeature.discount.discountAmount.value > 0) {
            return [
              {
                type: "FixedAmount",
                adType: getDisplayAdType(discountFeature.adType, country),
                value: discountFeature.discount.discountAmount.value
              }
            ];
          }
          return [];
        }
      )
    ] : []
  ).flat();
  const hasFixedAmountDiscounts = discounts.some(
    (discount) => discount.type === "FixedAmount"
  );
  let discountsToDisplay = discounts;
  if (isBrandedFeaturesFeaturePresent && !hasFixedAmountDiscounts) {
    discountsToDisplay = discounts.filter(
      (discount) => discount.adType !== "Classic" && discount.adType !== "Basic"
    );
  }
  if (!purchasableProducts) {
    throw new InvalidBudgetOptionForPresentationError(
      "No purchasable products"
    );
  }
  let productToDisplay = "";
  if (purchasableProducts.__typename === "PurchasableProductsList" && purchasableProducts.products) {
    productToDisplay = purchasableProducts.products;
  }
  if (purchasableProducts.__typename === "PurchasableProductsString" && purchasableProducts.product) {
    productToDisplay = purchasableProducts.product;
  }
  const durationUnit = durationFeature.unit.toLowerCase();
  if (durationUnit !== "month") {
    throw new InvalidBudgetOptionForPresentationError(
      `Unsupported duration unit: ${durationFeature.unit}`
    );
  }
  const isPrice = (price) => price.__typename === "Price";
  if (!isPrice(adBudgetProduct.price)) {
    throw new InvalidBudgetOptionForPresentationError(`Invalid price type`);
  }
  return {
    budgetValue: adBudgetProduct.price.netPrice.value,
    currency: adBudgetProduct.price.netPrice.currency,
    taxCode: (_a = adBudgetProduct.price.tax) == null ? void 0 : _a.code,
    taxValue: (_c = (_b = adBudgetProduct.price.tax) == null ? void 0 : _b.amount) == null ? void 0 : _c.value,
    taxDisplayRecommendation: (_d = adBudgetProduct.price.tax) == null ? void 0 : _d.displayRecommendation,
    adPostingFrequency,
    budgetId: adBudgetProduct.id,
    budgetName: adBudgetProduct.name,
    budgetType: isBrandedFeaturesFeaturePresent ? "BrandedBudget" : "FlexibleBudget",
    discounts: discountsToDisplay,
    duration: {
      value: durationFeature.value,
      unit: durationUnit
    },
    adRange,
    purchasableProducts: productToDisplay
  };
};
const useAdBudgetProductsForUpsell = ({
  advertiserId,
  productIds,
  productSetId,
  selectedBudgetProductId,
  country
}) => {
  const handleError = useErrorHandler();
  const upsellBudgetAlreadySelected = productIds.some(
    (productId) => productId === selectedBudgetProductId
  );
  const {
    data: upsellQueryData,
    error: upsellQueryError,
    loading: upsellQueryLoading
  } = useAdBudgetProductsForUpsellQuery({
    variables: { input: { advertiserId, productIds, productSetId } },
    skip: upsellBudgetAlreadySelected
  });
  const adBudgetProductsForUpsell = upsellQueryData == null ? void 0 : upsellQueryData.adBudgetProductsForUpsell;
  if (upsellQueryError) {
    handleError(new GetProductSetError(upsellQueryError.message));
  }
  if ((adBudgetProductsForUpsell == null ? void 0 : adBudgetProductsForUpsell.__typename) === "ProductSetExpiredError" || (adBudgetProductsForUpsell == null ? void 0 : adBudgetProductsForUpsell.__typename) === "ProductSetNotFoundError") {
    handleError(new GetProductSetError(adBudgetProductsForUpsell.message));
  }
  const upsellItems = (adBudgetProductsForUpsell == null ? void 0 : adBudgetProductsForUpsell.__typename) === "AdBudgetProductList" ? adBudgetProductsForUpsell == null ? void 0 : adBudgetProductsForUpsell.items : void 0;
  const adBudgetOptions = upsellItems ? upsellItems.map(
    (adBudgetProduct) => createBudgetPresentation(adBudgetProduct, country)
  ) : [];
  const shouldHideUpsell = !upsellQueryData || !adBudgetOptions.length;
  const mostNumberOfDiscounts = adBudgetOptions.reduce((most, budgetOption) => {
    var _a;
    const numberOfDiscounts = ((_a = budgetOption.discounts) == null ? void 0 : _a.length) ?? 0;
    return numberOfDiscounts > most ? numberOfDiscounts : most;
  }, 0);
  return {
    upsellQueryLoading,
    adBudgetOptions,
    shouldHideUpsell,
    mostNumberOfDiscounts
  };
};
const Upsell = (props) => {
  var _a;
  const {
    advertiserId,
    selectedBudgetProductId,
    onSelectedBudgetChange,
    onTrackingCallback,
    hubbleTrackEventHandler,
    onBudgetOptionClicked,
    styles,
    locale,
    productIds,
    productSetId,
    opportunityId,
    country,
    loadingProductIds,
    useUnifiedTracking = false
  } = props;
  const theme = useThemeName();
  const {
    upsellQueryLoading,
    adBudgetOptions,
    mostNumberOfDiscounts,
    shouldHideUpsell
  } = useAdBudgetProductsForUpsell({
    advertiserId,
    productIds,
    productSetId,
    selectedBudgetProductId,
    country
  });
  const adBudgetType = (_a = adBudgetOptions[0]) == null ? void 0 : _a.budgetType;
  const previousAdBudgetTypeRef = useRef();
  useEffect(() => {
    const isOptionAvailable = adBudgetOptions.some(
      (adBudget) => adBudget.budgetId === selectedBudgetProductId
    );
    const shouldResetSelection = !upsellQueryLoading && selectedBudgetProductId && (shouldHideUpsell || !isOptionAvailable);
    if (shouldResetSelection) {
      onSelectedBudgetChange(null, null);
    }
  }, [
    adBudgetOptions,
    selectedBudgetProductId,
    shouldHideUpsell,
    upsellQueryLoading,
    onSelectedBudgetChange
  ]);
  if (adBudgetType && adBudgetType !== previousAdBudgetTypeRef.current) {
    sendImpressionTracking({
      productSetId,
      onTrackingCallback,
      adBudgetOptions,
      hubbleTrackEventHandler,
      useUnifiedTracking,
      opportunityId
    });
    previousAdBudgetTypeRef.current = adBudgetType;
  }
  return /* @__PURE__ */ jsx(
    UpsellView,
    {
      adBudgetOptions,
      adBudgetType,
      mostNumberOfDiscounts,
      isQueryLoading: upsellQueryLoading,
      shouldHideUpsell,
      theme,
      selectedBudgetProductId,
      onSelectedBudgetChange,
      onTrackingCallback,
      hubbleTrackEventHandler,
      onBudgetOptionClicked,
      styles,
      locale,
      productSetId,
      opportunityId,
      useUnifiedTracking,
      loadingProductIds
    }
  );
};
const defaultLogger = {
  /* eslint-disable no-console */
  warn: console.warn,
  error: console.error,
  info: console.info
  /* eslint-enable no-console */
};
const LoggerContext = React.createContext(defaultLogger);
const LoggerProvider = ({ children, context }) => /* @__PURE__ */ jsx(
  LoggerContext.Provider,
  {
    value: {
      ...defaultLogger,
      ...context
    },
    children
  }
);
const HirerBudgetUpsell = (props) => {
  const { logger, onError } = props;
  return /* @__PURE__ */ jsx(LoggerProvider, { context: logger, children: /* @__PURE__ */ jsx(
    ErrorBoundary,
    {
      FallbackComponent: () => null,
      onError: (error) => {
        if (typeof onError === "function") {
          onError(error);
        }
      },
      children: /* @__PURE__ */ jsx(Upsell, { ...props })
    }
  ) });
};
const useCreateAdBudgetData = ({ country, upsellItems }) => {
  const adBudgetOptions = upsellItems ? upsellItems.map(
    (adBudgetProduct) => createBudgetPresentation(adBudgetProduct, country)
  ) : [];
  const shouldHideUpsell = !upsellItems || !adBudgetOptions.length;
  const mostNumberOfDiscounts = adBudgetOptions.reduce((most, budgetOption) => {
    var _a;
    const numberOfDiscounts = ((_a = budgetOption.discounts) == null ? void 0 : _a.length) ?? 0;
    return numberOfDiscounts > most ? numberOfDiscounts : most;
  }, 0);
  return {
    adBudgetOptions,
    shouldHideUpsell,
    mostNumberOfDiscounts
  };
};
const HirerBudgetUpsellConnectedCart = ({
  shoppingCart,
  ...props
}) => {
  var _a;
  const theme = useThemeName();
  const shoppingCartContext = useContext(ShoppingCartContext);
  const {
    subscriptionResponseKey,
    key: subscriptionUniqueIdKey,
    getProductsDocument,
    getProductsDocumentConstructVariables
  } = shoppingCart;
  const {
    productResponses,
    subscribeForProducts,
    unsubscribeForProducts,
    addLineItem,
    removeLineItem,
    cartProductInProgress,
    cartState: { productSetId, items } = {}
  } = shoppingCartContext;
  const client = useApolloClient();
  const getProductIdsQuery = useCallback(
    async (productSetIdParam) => await client.query({
      query: getProductsDocument,
      variables: getProductsDocumentConstructVariables(productSetIdParam)
    }),
    [getProductsDocument, getProductsDocumentConstructVariables, client]
  );
  useEffect(() => {
    subscribeForProducts(
      subscriptionUniqueIdKey,
      subscriptionResponseKey,
      getProductIdsQuery
    );
    return () => {
      if (typeof unsubscribeForProducts !== "undefined")
        unsubscribeForProducts(subscriptionUniqueIdKey);
    };
  }, [
    getProductIdsQuery,
    subscribeForProducts,
    subscriptionUniqueIdKey,
    subscriptionResponseKey,
    unsubscribeForProducts
  ]);
  const { products, loading, error } = productResponses[subscriptionUniqueIdKey] ?? {};
  const {
    onTrackingCallback,
    hubbleTrackEventHandler,
    onBudgetOptionClicked,
    styles,
    locale,
    opportunityId,
    country,
    useUnifiedTracking = false,
    logger,
    onError
  } = props;
  const selectedBudgetProductId = (items == null ? void 0 : items.filter((product) => product.product.__typename === "AdBudgetCartItem").map((product) => product.product.id)[0]) ?? null;
  const loadingProductIds = Object.keys(cartProductInProgress);
  const onSelectedBudgetChange = useCallback(
    (budgetProductId, budgetName) => {
      props.onSelectedBudgetChange(budgetProductId, budgetName);
      if (budgetProductId && budgetProductId !== selectedBudgetProductId) {
        addLineItem({
          productId: budgetProductId,
          productType: "AdBudgetProduct"
        });
        return;
      }
      if (!budgetProductId && selectedBudgetProductId) {
        removeLineItem({
          productId: selectedBudgetProductId,
          productType: "AdBudgetProduct"
        });
        return;
      }
    },
    [addLineItem, props, removeLineItem, selectedBudgetProductId]
  );
  const upsellItems = (products == null ? void 0 : products.__typename) === "AdBudgetProductList" ? products == null ? void 0 : products.items : void 0;
  const { adBudgetOptions, mostNumberOfDiscounts, shouldHideUpsell } = useCreateAdBudgetData({
    upsellItems,
    country
  });
  const adBudgetType = (_a = adBudgetOptions[0]) == null ? void 0 : _a.budgetType;
  const previousAdBudgetTypeRef = useRef();
  useEffect(() => {
    const isOptionAvailable = adBudgetOptions.some(
      (adBudget) => adBudget.budgetId === selectedBudgetProductId
    );
    const shouldResetSelection = !loading && selectedBudgetProductId && (shouldHideUpsell || !isOptionAvailable);
    if (shouldResetSelection) {
      onSelectedBudgetChange(null, null);
    }
  }, [
    adBudgetOptions,
    selectedBudgetProductId,
    shouldHideUpsell,
    loading,
    onSelectedBudgetChange
  ]);
  if (adBudgetType && adBudgetType !== previousAdBudgetTypeRef.current) {
    sendImpressionTracking({
      productSetId,
      onTrackingCallback,
      adBudgetOptions,
      hubbleTrackEventHandler,
      useUnifiedTracking,
      opportunityId
    });
    previousAdBudgetTypeRef.current = adBudgetType;
  }
  if (error) {
    return null;
  }
  return /* @__PURE__ */ jsx(LoggerProvider, { context: logger, children: /* @__PURE__ */ jsx(
    ErrorBoundary,
    {
      FallbackComponent: () => null,
      onError: (errorBoundaryError) => {
        if (typeof onError === "function") {
          onError(errorBoundaryError);
        }
      },
      children: /* @__PURE__ */ jsx(
        UpsellView,
        {
          adBudgetOptions,
          adBudgetType,
          mostNumberOfDiscounts,
          isQueryLoading: loading,
          shouldHideUpsell,
          theme,
          selectedBudgetProductId,
          onSelectedBudgetChange,
          onTrackingCallback,
          hubbleTrackEventHandler,
          onBudgetOptionClicked,
          styles,
          locale,
          productSetId,
          opportunityId,
          useUnifiedTracking,
          loadingProductIds
        }
      )
    }
  ) });
};
export {
  AdType,
  HirerBudgetUpsell,
  HirerBudgetUpsellConnectedCart,
  ProductTypeInput
};
